<script setup lang="ts">
import type { SelectItemProps } from 'reka-ui';
import type { HTMLAttributes } from 'vue';
import { reactiveOmit } from '@vueuse/core';
import { Check } from 'lucide';
import { SelectItem, SelectItemIndicator, SelectItemText, useForwardProps } from 'reka-ui';
import SphIcon from '@/components/base/icon';

defineOptions({
    compatConfig: {
        MODE: 3,
    },
});

const props = defineProps<SelectItemProps & {
    class?: HTMLAttributes['class']
    hasIndicator?: boolean
}>();

const delegatedProps = reactiveOmit(props, 'class');

const forwardedProps = useForwardProps(delegatedProps);
</script>

<template>
    <SelectItem
        data-slot="select-item"
        class="select-item"
        v-bind="forwardedProps"
        :class="[props.class, {
            'has-indicator': props.hasIndicator,
        }]"
    >
        <span
            v-if="props.hasIndicator"
            class="indicator"
        >
            <SelectItemIndicator>
                <slot name="indicator-icon">
                    <SphIcon
                        :icon="Check"
                        size="xs"
                    />
                </slot>
            </SelectItemIndicator>
        </span>

        <SelectItemText
            class="flex items-center w-full gap-2"
        >
            <slot></slot>
        </SelectItemText>

        <div class="back"></div>
    </SelectItem>
</template>

<style scoped>
@reference '@/assets/styles/index.css';

.select-item {
    @apply bg-popover border border-transparent [&_svg:not([class*=\'text-\'])]:text-muted-foreground relative flex w-full cursor-default items-center gap-2 rounded-sm py-1.5 px-2 text-sm font-medium outline-hidden select-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*=\'size-\'])]:size-4 *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2;

    &:hover,
    &:focus-visible {
        .back {
            @apply opacity-100;
        }
    }

    &[aria-selected='true'] {
        .back {
            @apply opacity-100 bg-accent-light! text-accent-light-foreground! border-accent-light;
        }

        &:hover,
        &:focus-visible {
            .back {
                @apply border-ring/50;
            }
        }
    }

    &[data-disabled] {
        @apply pointer-events-none opacity-50;
    }

    &.has-indicator {
        @apply pr-8;
    }

    > * {
        @apply relative z-10;
    }
}

.back {
    @apply absolute inset-[1px_0] z-1 rounded-md bg-primary/5 border border-ring/50 opacity-0;
}

.indicator {
    @apply absolute right-2 flex size-3.5 items-center justify-center;
}
</style>
